// skills/combat/combatData.js
// Combat zones, mobs, loot tables, and configuration

// ==================== COMBAT ZONES ====================
// 3 tiers with 3 zones each (9 total) + 1 tiered endgame zone (The Ashen Crucible)

export const COMBAT_ZONES = {
    // ========== Tier 1 - Levels 1-25 ==========
    // No Pyre requirements for Tier 1
    GOBLIN_CAMP: {
        id: 'goblin_camp',
        name: 'Goblin Camp',
        tier: 1,
        level: 1,
        mobs: ['goblin_raider', 'goblin_shaman', 'goblin_skirmisher', 'camp_chicken', 'goblin_herbalist', 'ember_goblin', 'goblin_trapper', 'goblin_looter', 'goblin_firestarter'],
        color: '#4a7c4a',
        description: 'A crude encampment of petty thieves and scavengers.',
        clearThreshold: 50,  // Kills to unlock target farming
        pyreLevel: null      // No Pyre requirement
    },
    FORGOTTEN_OUTSKIRTS: {
        id: 'forgotten_outskirts',
        name: 'Forgotten Outskirts',
        tier: 1,
        level: 10,
        mobs: ['scavenger', 'stray_mercenary', 'feral_beast', 'carrion_hawk', 'ruined_sentry', 'marsh_forager', 'wild_hound'],
        color: '#6b6b6b',
        description: 'Abandoned ruins where outcasts and scavengers roam.',
        clearThreshold: 50,
        pyreLevel: null
    },
    BANDIT_HIDEOUT: {
        id: 'bandit_hideout',
        name: 'Bandit Hideout',
        tier: 1,
        level: 20,
        mobs: ['bandit_thug', 'bandit_archer', 'bandit_enforcer', 'bandit_alchemist', 'black_market_runner', 'bandit_scout', 'bandit_smuggler', 'bandit_hexer'],
        color: '#8b4513',
        description: 'An abandoned fort occupied by outlaws.',
        clearThreshold: 50,
        pyreLevel: null
    },

    // ========== Tier 2 - The Depths (Levels 30-55) ==========
    // Pyre requirements: penalty below, bonus above (up to +10% at 99)
    TROLL_CAVES: {
        id: 'troll_caves',
        name: 'Troll Caves',
        tier: 2,
        level: 30,
        mobs: ['cave_troll', 'troll_brute', 'regenerating_troll', 'crystal_lurker', 'ashbound_troll', 'fungal_crawler', 'stone_golem', 'cave_spider'],
        color: '#556b2f',
        description: 'Deep caverns home to regenerating brutes.',
        clearThreshold: 75,
        pyreLevel: 20        // Soft penalty below Pyre 20
    },
    UNDEAD_CRYPT: {
        id: 'undead_crypt',
        name: 'Undead Crypt',
        tier: 2,
        level: 40,
        mobs: ['skeleton_warrior', 'rotting_zombie', 'crypt_acolyte', 'tomb_weaver', 'scorched_revenant', 'spectral_archer', 'bone_collector', 'cursed_knight'],
        color: '#3d3d3d',
        description: 'Ancient burial grounds where the dead do not rest.',
        clearThreshold: 75,
        pyreLevel: 30
    },
    DEMON_SCAR: {
        id: 'demon_scar',
        name: 'Demon Scar',
        tier: 2,
        level: 50,
        mobs: ['lesser_demon', 'flame_imp', 'scarred_cultist', 'infernal_arcanist', 'cinderbound_cultist', 'pit_fiend', 'soul_harvester', 'hellhound'],
        color: '#8b0000',
        description: 'A rift to the infernal planes, leaking dark entities.',
        clearThreshold: 75,
        pyreLevel: 40
    },

    // ========== Tier 3 - Levels 60+ ==========
    // No Pyre requirements for Tier 3
    DRAGON_LAIR: {
        id: 'dragon_lair',
        name: 'Dragon Lair',
        tier: 3,
        level: 60,
        mobs: ['wyrmling', 'ancient_dragon', 'draconic_sentinel', 'hoard_guardian', 'molten_wyrm', 'drake_whelp', 'dragon_priest', 'scaled_marauder'],
        color: '#ff4500',
        description: 'A volcanic cavern guarded by ancient wyrms.',
        clearThreshold: 100,
        pyreLevel: null
    },
    VOID_RIFT: {
        id: 'void_rift',
        name: 'Void Rift',
        tier: 3,
        level: 75,
        mobs: ['void_aberration', 'rift_horror', 'void_channeler', 'rift_weaver', 'void_ash_horror', 'void_stalker', 'rift_scavenger', 'entropy_maw'],
        color: '#4b0082',
        description: 'A tear in reality seeping with cosmic nightmares.',
        clearThreshold: 100,
        pyreLevel: null
    },
    ANCIENT_SANCTUM: {
        id: 'ancient_sanctum',
        name: 'Ancient Sanctum',
        tier: 3,
        level: 80,
        mobs: ['elder_construct', 'corrupted_avatar', 'elder_god_fragment', 'elder_herbkeeper', 'sanctum_guardian', 'ancient_scribe', 'temporal_shade', 'relic_hoarder'],
        color: '#ffd700',
        description: 'A forgotten temple where eldritch powers dwell.',
        clearThreshold: 100,
        pyreLevel: null
    },

    // ========== Tier 4 - The Ashen Crucible (Tiered Endgame Zone) ==========
    // Single endgame zone with 3 progression tiers, each gated by clearing the previous
    ASHEN_CRUCIBLE: {
        id: 'ashen_crucible',
        name: 'The Ashen Crucible',
        tier: 4,
        level: 65,
        color: '#ff4500',
        description: 'The ultimate trial of flame. Three tiers of escalating challenge await.',
        pyreLevel: 70,
        pyreVeilHidden: true,
        tiers: {
            t1: {
                name: 'The Embered Halls',
                mobs: ['ashen_warden', 'pyre_guardian', 'flame_eternal'],
                unlock: 'zone_ashen_crucible_t1',
                clearThreshold: 125
            },
            t2: {
                name: 'The Searing Tribunal',
                mobs: ['searing_matriarch', 'molten_colossus', 'cindered_knight'],
                unlock: 'zone_ashen_crucible_t2',
                clearThreshold: 125
            },
            t3: {
                name: 'The Convergence',
                mobs: ['convergence_herald'],
                unlock: 'zone_ashen_crucible_t3',
                clearThreshold: 1
            }
        }
    }
};

// ==================== MOBS ====================
// Each mob has HP, attack, defense, attack speed, accuracy, weaknesses, and XP reward
// weakTo/resistantTo: 'melee', 'ranged', or 'magic' (null for neutral)
// - weakTo: Player gets +25% accuracy and +15% damage with that style
// - resistantTo: Player gets -25% accuracy and -15% damage with that style
// elementalWeakness: 'fire', 'ice', 'earth', or 'air' (array for multiple)
// - Spells matching the element deal +20% bonus damage

export const MOBS = {
    // ========== Tier 1 Mobs - Goblin Camp (Lv 1) ==========
    GOBLIN_RAIDER: {
        id: 'goblin_raider',
        name: 'Goblin Raider',
        tier: 1,
        hp: 45,
        attack: 2,
        attackMax: 4,
        defense: 1,
        accuracy: 60,
        attackSpeed: 3500,
        xp: 15,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },
    GOBLIN_SHAMAN: {
        id: 'goblin_shaman',
        name: 'Goblin Shaman',
        tier: 1,
        hp: 36,
        attack: 3,
        attackMax: 5,
        defense: 0,
        accuracy: 65,
        attackSpeed: 3800,
        xp: 18,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    GOBLIN_SKIRMISHER: {
        id: 'goblin_skirmisher',
        name: 'Goblin Skirmisher',
        tier: 1,
        hp: 54,
        attack: 2,
        attackMax: 3,
        defense: 2,
        accuracy: 70,
        attackSpeed: 2800,
        xp: 16,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },
    CAMP_CHICKEN: {
        id: 'camp_chicken',
        name: 'Camp Chicken',
        tier: 1,
        hp: 15,
        attack: 1,
        attackMax: 2,
        defense: 0,
        accuracy: 40,
        attackSpeed: 2000,
        xp: 5,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // ========== Tier 1 Mobs - Forgotten Outskirts (Lv 10) ==========
    SCAVENGER: {
        id: 'scavenger',
        name: 'Scavenger',
        tier: 1,
        hp: 66,
        attack: 3,
        attackMax: 5,
        defense: 2,
        accuracy: 60,
        attackSpeed: 3200,
        xp: 22,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },
    STRAY_MERCENARY: {
        id: 'stray_mercenary',
        name: 'Stray Mercenary',
        tier: 1,
        hp: 90,
        attack: 4,
        attackMax: 6,
        defense: 4,
        accuracy: 68,
        attackSpeed: 3400,
        xp: 28,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['ice']
    },
    FERAL_BEAST: {
        id: 'feral_beast',
        name: 'Feral Beast',
        tier: 1,
        hp: 75,
        attack: 4,
        attackMax: 7,
        defense: 2,
        accuracy: 65,
        attackSpeed: 2600,
        xp: 25,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },

    // ========== Tier 1 Mobs - Bandit Hideout (Lv 20) ==========
    BANDIT_THUG: {
        id: 'bandit_thug',
        name: 'Bandit Thug',
        tier: 1,
        hp: 135,
        attack: 5,
        attackMax: 8,
        defense: 6,
        accuracy: 65,
        attackSpeed: 3600,
        xp: 38,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['ice']
    },
    BANDIT_ARCHER: {
        id: 'bandit_archer',
        name: 'Bandit Archer',
        tier: 1,
        hp: 105,
        attack: 6,
        attackMax: 9,
        defense: 3,
        accuracy: 75,
        attackSpeed: 3200,
        xp: 40,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },
    BANDIT_ENFORCER: {
        id: 'bandit_enforcer',
        name: 'Bandit Enforcer',
        tier: 1,
        hp: 165,
        attack: 6,
        attackMax: 10,
        defense: 7,
        accuracy: 70,
        attackSpeed: 3800,
        xp: 48,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },

    // ========== Tier 2 Mobs - Troll Caves (Lv 30) ==========
    CAVE_TROLL: {
        id: 'cave_troll',
        name: 'Cave Troll',
        tier: 2,
        hp: 240,
        attack: 10,
        attackMax: 16,
        defense: 8,
        accuracy: 60,
        attackSpeed: 4500,
        xp: 65,
        weakTo: 'magic',
        resistantTo: 'ranged',
        elementalWeakness: ['fire']
    },
    TROLL_BRUTE: {
        id: 'troll_brute',
        name: 'Troll Brute',
        tier: 2,
        hp: 300,
        attack: 14,
        attackMax: 22,
        defense: 10,
        accuracy: 55,
        attackSpeed: 5000,
        xp: 78,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['fire']
    },
    REGENERATING_TROLL: {
        id: 'regenerating_troll',
        name: 'Regenerating Troll',
        tier: 2,
        hp: 360,
        attack: 8,
        attackMax: 14,
        defense: 6,
        accuracy: 58,
        attackSpeed: 4200,
        xp: 72,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['fire', 'air']
    },

    // ========== Tier 2 Mobs - Undead Crypt (Lv 40) ==========
    SKELETON_WARRIOR: {
        id: 'skeleton_warrior',
        name: 'Skeleton Warrior',
        tier: 2,
        hp: 210,
        attack: 15,
        attackMax: 22,
        defense: 14,
        accuracy: 72,
        attackSpeed: 3200,
        xp: 80,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['fire', 'earth']
    },
    ROTTING_ZOMBIE: {
        id: 'rotting_zombie',
        name: 'Rotting Zombie',
        tier: 2,
        hp: 285,
        attack: 12,
        attackMax: 20,
        defense: 8,
        accuracy: 55,
        attackSpeed: 4200,
        xp: 75,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['fire']
    },
    CRYPT_ACOLYTE: {
        id: 'crypt_acolyte',
        name: 'Crypt Acolyte',
        tier: 2,
        hp: 180,
        attack: 18,
        attackMax: 28,
        defense: 10,
        accuracy: 78,
        attackSpeed: 3600,
        xp: 88,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // ========== Tier 2 Mobs - Demon Scar (Lv 50) ==========
    LESSER_DEMON: {
        id: 'lesser_demon',
        name: 'Lesser Demon',
        tier: 2,
        hp: 390,
        attack: 22,
        attackMax: 35,
        defense: 16,
        accuracy: 75,
        attackSpeed: 3500,
        xp: 100,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    FLAME_IMP: {
        id: 'flame_imp',
        name: 'Flame Imp',
        tier: 2,
        hp: 255,
        attack: 25,
        attackMax: 38,
        defense: 8,
        accuracy: 80,
        attackSpeed: 2800,
        xp: 95,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    SCARRED_CULTIST: {
        id: 'scarred_cultist',
        name: 'Scarred Cultist',
        tier: 2,
        hp: 330,
        attack: 20,
        attackMax: 32,
        defense: 12,
        accuracy: 72,
        attackSpeed: 3400,
        xp: 92,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },

    // ========== Tier 3 Mobs - Dragon Lair (Lv 60) ==========
    WYRMLING: {
        id: 'wyrmling',
        name: 'Wyrmling',
        tier: 3,
        hp: 540,
        attack: 38,
        attackMax: 52,
        defense: 22,
        accuracy: 78,
        attackSpeed: 3400,
        xp: 160,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    ANCIENT_DRAGON: {
        id: 'ancient_dragon',
        name: 'Ancient Dragon',
        tier: 3,
        hp: 840,
        attack: 50,
        attackMax: 68,
        defense: 32,
        accuracy: 85,
        attackSpeed: 4200,
        xp: 220,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    DRACONIC_SENTINEL: {
        id: 'draconic_sentinel',
        name: 'Draconic Sentinel',
        tier: 3,
        hp: 660,
        attack: 42,
        attackMax: 58,
        defense: 28,
        accuracy: 82,
        attackSpeed: 3800,
        xp: 185,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },

    // ========== Tier 3 Mobs - Void Rift (Lv 75) ==========
    VOID_ABERRATION: {
        id: 'void_aberration',
        name: 'Void Aberration',
        tier: 3,
        hp: 780,
        attack: 48,
        attackMax: 62,
        defense: 28,
        accuracy: 78,
        attackSpeed: 3400,
        xp: 235,
        weakTo: 'magic',
        resistantTo: 'ranged',
        elementalWeakness: ['earth', 'air']
    },
    RIFT_HORROR: {
        id: 'rift_horror',
        name: 'Rift Horror',
        tier: 3,
        hp: 960,
        attack: 52,
        attackMax: 70,
        defense: 35,
        accuracy: 82,
        attackSpeed: 3600,
        xp: 275,
        weakTo: 'magic',
        resistantTo: 'ranged',
        elementalWeakness: ['earth']
    },
    VOID_CHANNELER: {
        id: 'void_channeler',
        name: 'Void Channeler',
        tier: 3,
        hp: 720,
        attack: 55,
        attackMax: 72,
        defense: 24,
        accuracy: 85,
        attackSpeed: 3200,
        xp: 260,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // ========== Tier 3 Mobs - Ancient Sanctum (Lv 90) ==========
    ELDER_CONSTRUCT: {
        id: 'elder_construct',
        name: 'Elder Construct',
        tier: 3,
        hp: 1140,
        attack: 55,
        attackMax: 72,
        defense: 40,
        accuracy: 85,
        attackSpeed: 4000,
        xp: 320,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['ice', 'air']
    },
    CORRUPTED_AVATAR: {
        id: 'corrupted_avatar',
        name: 'Corrupted Avatar',
        tier: 3,
        hp: 1260,
        attack: 58,
        attackMax: 75,
        defense: 38,
        accuracy: 88,
        attackSpeed: 3600,
        xp: 360,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },
    ELDER_GOD_FRAGMENT: {
        id: 'elder_god_fragment',
        name: 'Elder God Fragment',
        tier: 3,
        hp: 1500,
        attack: 62,
        attackMax: 80,
        defense: 45,
        accuracy: 92,
        attackSpeed: 3800,
        xp: 420,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // ========== Themed Mobs — Target Farming ==========
    // Each zone gets 1 themed mob focused on an underserved resource category.
    // These are the BEST combat source for their specialty, but skills remain primary.

    // Goblin Camp — herbs + seeds
    GOBLIN_HERBALIST: {
        id: 'goblin_herbalist',
        name: 'Goblin Herbalist',
        tier: 1,
        hp: 30,
        attack: 2,
        attackMax: 4,
        defense: 0,
        accuracy: 55,
        attackSpeed: 4000,
        xp: 16,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // Forgotten Outskirts — feathers + raw fish
    CARRION_HAWK: {
        id: 'carrion_hawk',
        name: 'Carrion Hawk',
        tier: 1,
        hp: 48,
        attack: 3,
        attackMax: 6,
        defense: 1,
        accuracy: 75,
        attackSpeed: 2400,
        xp: 24,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },

    // Bandit Hideout — potions + mid herbs + vials
    BANDIT_ALCHEMIST: {
        id: 'bandit_alchemist',
        name: 'Bandit Alchemist',
        tier: 1,
        hp: 90,
        attack: 6,
        attackMax: 10,
        defense: 3,
        accuracy: 68,
        attackSpeed: 3400,
        xp: 42,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // Troll Caves — gems + ores
    CRYSTAL_LURKER: {
        id: 'crystal_lurker',
        name: 'Crystal Lurker',
        tier: 2,
        hp: 255,
        attack: 8,
        attackMax: 13,
        defense: 8,
        accuracy: 58,
        attackSpeed: 4200,
        xp: 68,
        weakTo: 'magic',
        resistantTo: 'ranged',
        elementalWeakness: ['fire']
    },

    // Undead Crypt — cloth + low glyphs
    TOMB_WEAVER: {
        id: 'tomb_weaver',
        name: 'Tomb Weaver',
        tier: 2,
        hp: 195,
        attack: 15,
        attackMax: 24,
        defense: 6,
        accuracy: 72,
        attackSpeed: 3200,
        xp: 82,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // Demon Scar — glyphs (flame, entropy) + ichor
    INFERNAL_ARCANIST: {
        id: 'infernal_arcanist',
        name: 'Infernal Arcanist',
        tier: 2,
        hp: 285,
        attack: 20,
        attackMax: 30,
        defense: 8,
        accuracy: 75,
        attackSpeed: 3200,
        xp: 95,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },

    // Dragon Lair — high gems + gold/bars
    HOARD_GUARDIAN: {
        id: 'hoard_guardian',
        name: 'Hoard Guardian',
        tier: 3,
        hp: 600,
        attack: 35,
        attackMax: 50,
        defense: 30,
        accuracy: 72,
        attackSpeed: 4000,
        xp: 170,
        weakTo: 'magic',
        resistantTo: 'ranged',
        elementalWeakness: ['fire', 'earth']
    },

    // Void Rift — high cloth + high glyphs
    RIFT_WEAVER: {
        id: 'rift_weaver',
        name: 'Rift Weaver',
        tier: 3,
        hp: 750,
        attack: 50,
        attackMax: 68,
        defense: 20,
        accuracy: 82,
        attackSpeed: 3000,
        xp: 255,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // Ancient Sanctum — endgame herbs + potions
    ELDER_HERBKEEPER: {
        id: 'elder_herbkeeper',
        name: 'Elder Herbkeeper',
        tier: 3,
        hp: 1140,
        attack: 55,
        attackMax: 72,
        defense: 32,
        accuracy: 80,
        attackSpeed: 3600,
        xp: 380,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },

    // ========== Pyre Veil Hidden Mobs ==========
    // These mobs are hidden until player reaches required Pyre level
    EMBER_GOBLIN: {
        id: 'ember_goblin',
        name: 'Ember Goblin',
        tier: 1,
        hp: 84,
        attack: 5,
        attackMax: 8,
        defense: 4,
        accuracy: 72,
        attackSpeed: 2900,
        xp: 35,
        weakTo: 'ranged',
        resistantTo: 'magic',
        pyreVeilLevel: 10
    },
    BLACK_MARKET_RUNNER: {
        id: 'black_market_runner',
        name: 'Black Market Runner',
        tier: 1,
        hp: 180,
        attack: 8,
        attackMax: 13,
        defense: 8,
        accuracy: 78,
        attackSpeed: 2600,
        xp: 55,
        weakTo: 'melee',
        resistantTo: 'ranged',
        pyreVeilLevel: 15
    },
    ASHBOUND_TROLL: {
        id: 'ashbound_troll',
        name: 'Ashbound Troll',
        tier: 2,
        hp: 420,
        attack: 16,
        attackMax: 26,
        defense: 12,
        accuracy: 62,
        attackSpeed: 4800,
        xp: 95,
        weakTo: 'magic',
        resistantTo: 'melee',
        pyreVeilLevel: 25,
        elementalWeakness: ['ice']
    },
    SCORCHED_REVENANT: {
        id: 'scorched_revenant',
        name: 'Scorched Revenant',
        tier: 2,
        hp: 315,
        attack: 22,
        attackMax: 35,
        defense: 15,
        accuracy: 76,
        attackSpeed: 3400,
        xp: 110,
        weakTo: 'magic',
        resistantTo: 'melee',
        pyreVeilLevel: 35,
        elementalWeakness: ['ice', 'earth']
    },
    CINDERBOUND_CULTIST: {
        id: 'cinderbound_cultist',
        name: 'Cinderbound Cultist',
        tier: 2,
        hp: 435,
        attack: 28,
        attackMax: 42,
        defense: 18,
        accuracy: 80,
        attackSpeed: 3200,
        xp: 125,
        weakTo: 'ranged',
        resistantTo: 'magic',
        pyreVeilLevel: 45
    },
    MOLTEN_WYRM: {
        id: 'molten_wyrm',
        name: 'Molten Wyrm',
        tier: 3,
        hp: 720,
        attack: 45,
        attackMax: 62,
        defense: 28,
        accuracy: 82,
        attackSpeed: 3600,
        xp: 200,
        weakTo: 'ranged',
        resistantTo: 'magic',
        pyreVeilLevel: 55
    },
    VOID_ASH_HORROR: {
        id: 'void_ash_horror',
        name: 'Void Ash Horror',
        tier: 3,
        hp: 1020,
        attack: 56,
        attackMax: 74,
        defense: 36,
        accuracy: 84,
        attackSpeed: 3500,
        xp: 295,
        weakTo: 'magic',
        resistantTo: 'ranged',
        pyreVeilLevel: 65,
        elementalWeakness: ['earth', 'ice']
    },

    // ========== Cindered Reliquary Dungeon Bosses ==========
    ASHEN_WARDEN: {
        id: 'ashen_warden',
        name: 'Ashen Warden',
        tier: 4,
        hp: 4000,
        attack: 65,
        attackMax: 85,
        defense: 48,
        accuracy: 82,
        attackSpeed: 4000,
        xp: 580,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['ice', 'air'],
        isBoss: true,
        enrageTimer: 90,
        enrageMultiplier: 1.5,
        enrageSpeedMult: 1.25
    },
    PYRE_GUARDIAN: {
        id: 'pyre_guardian',
        name: 'Pyre Guardian',
        tier: 4,
        hp: 6000,
        attack: 75,
        attackMax: 100,
        defense: 55,
        accuracy: 85,
        attackSpeed: 3800,
        xp: 750,
        weakTo: 'melee',
        resistantTo: 'magic',
        isBoss: true,
        healingPulse: { amount: 60, interval: 20 },
        phases: [
            { hpPercent: 50, effects: { defenseChange: 15, defensePhaseDuration: 30 } }
        ]
    },
    FLAME_ETERNAL: {
        id: 'flame_eternal',
        name: 'Flame Eternal',
        tier: 4,
        hp: 12000,
        attack: 85,
        attackMax: 105,
        defense: 60,
        accuracy: 88,
        attackSpeed: 4200,
        xp: 950,
        weakTo: 'ranged',
        resistantTo: 'melee',
        isBoss: true,
        damageFloor: 15,
        phases: [
            { hpPercent: 66, effects: { playerDot: 5, playerDotInterval: 3, attackSpeedMult: 1.2 } },
            { hpPercent: 33, effects: { damageMult: 1.4, defenseChange: -15 } }
        ]
    },

    // ========== Set Bosses (The Ashen Crucible — Tier 2) ==========
    // Inverse weakness/drop triangle:
    //   Searing Matriarch (weak melee) → drops Pyreweave (magic) set
    //   Molten Colossus (weak magic) → drops Cindermark (ranged) set
    //   Cindered Knight (weak ranged) → drops Ashforged (melee) set
    SEARING_MATRIARCH: {
        id: 'searing_matriarch',
        name: 'Searing Matriarch',
        tier: 4,
        hp: 12500,
        attack: 90,
        attackMax: 115,
        defense: 55,
        accuracy: 85,
        attackSpeed: 3600,
        xp: 1200,
        weakTo: 'melee',
        resistantTo: 'ranged',
        isBoss: true,
        damageFloor: 18,
        phases: [
            { hpPercent: 50, effects: { trueDamage: 8, trueDamageInterval: 5, attackSpeedMult: 1.2 } }
        ]
    },
    MOLTEN_COLOSSUS: {
        id: 'molten_colossus',
        name: 'Molten Colossus',
        tier: 4,
        hp: 16000,
        attack: 80,
        attackMax: 100,
        defense: 60,
        accuracy: 88,
        attackSpeed: 3400,
        xp: 1400,
        weakTo: 'magic',
        resistantTo: 'melee',
        elementalWeakness: ['ice'],
        isBoss: true,
        damageFloor: 20,
        phases: [
            { hpPercent: 60, effects: { defenseChange: 10, healingPulse: { amount: 40, interval: 15 } } },
            { hpPercent: 30, effects: { defenseChange: 20, healingPulse: { amount: 80, interval: 15 } } }
        ]
    },
    CINDERED_KNIGHT: {
        id: 'cindered_knight',
        name: 'Cindered Knight',
        tier: 4,
        hp: 18000,
        attack: 100,
        attackMax: 110,
        defense: 65,
        accuracy: 90,
        attackSpeed: 4600,
        xp: 1600,
        weakTo: 'ranged',
        resistantTo: 'magic',
        isBoss: true,
        damageFloor: 22,
        enrageTimer: 75,
        enrageMultiplier: 1.4,
        enrageSpeedMult: 1.2,
        phases: [
            { hpPercent: 40, effects: { damageMult: 1.2, defenseIgnore: 0.3 } }
        ]
    },

    // ========== Ashen Convergence Raid Boss ==========
    // Final boss - HP balanced for 4-5 minute kills pre-Eclipse, ~60-90s with Eclipse
    // Uses 6x multiplier for endgame challenge
    CONVERGENCE_HERALD: {
        id: 'convergence_herald',
        name: 'Convergence Herald',
        tier: 5,
        hp: 35000,
        attack: 100,
        attackMax: 125,
        defense: 75,
        accuracy: 92,
        attackSpeed: 4000,
        xp: 2500,
        weakTo: null,
        resistantTo: null,
        isBoss: true,
        damageFloor: 25,
        enrageTimer: 180,
        enrageSpeedMult: 2.0,
        phases: [
            { hpPercent: 75, effects: { playerDamageReduction: 0.25, attackSpeedMult: 1.15 } },
            { hpPercent: 50, effects: { trueDamage: 40, trueDamageInterval: 10, healingPulse: { amount: 100, interval: 10 } } },
            { hpPercent: 25, effects: { damageMult: 1.3, removePlayerDamageReduction: true } }
        ]
    },

    // ==================== NEW MOBS - Combat Expansion ====================

    // ========== Goblin Camp - New Mobs (Lv 1) ==========
    GOBLIN_TRAPPER: {
        id: 'goblin_trapper',
        name: 'Goblin Trapper',
        tier: 1,
        hp: 40,
        attack: 2,
        attackMax: 4,
        defense: 1,
        accuracy: 60,
        attackSpeed: 3200,
        xp: 14,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },
    GOBLIN_LOOTER: {
        id: 'goblin_looter',
        name: 'Goblin Looter',
        tier: 1,
        hp: 50,
        attack: 2,
        attackMax: 3,
        defense: 2,
        accuracy: 62,
        attackSpeed: 3000,
        xp: 17,
        weakTo: 'magic',
        resistantTo: 'ranged'
    },
    GOBLIN_FIRESTARTER: {
        id: 'goblin_firestarter',
        name: 'Goblin Firestarter',
        tier: 1,
        hp: 35,
        attack: 3,
        attackMax: 6,
        defense: 0,
        accuracy: 65,
        attackSpeed: 3600,
        xp: 18,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },

    // ========== Forgotten Outskirts - New Mobs (Lv 10) ==========
    RUINED_SENTRY: {
        id: 'ruined_sentry',
        name: 'Ruined Sentry',
        tier: 1,
        hp: 80,
        attack: 3,
        attackMax: 5,
        defense: 5,
        accuracy: 58,
        attackSpeed: 3800,
        xp: 26,
        weakTo: 'magic',
        resistantTo: 'melee'
    },
    MARSH_FORAGER: {
        id: 'marsh_forager',
        name: 'Marsh Forager',
        tier: 1,
        hp: 55,
        attack: 3,
        attackMax: 5,
        defense: 1,
        accuracy: 60,
        attackSpeed: 3400,
        xp: 23,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    WILD_HOUND: {
        id: 'wild_hound',
        name: 'Wild Hound',
        tier: 1,
        hp: 60,
        attack: 4,
        attackMax: 6,
        defense: 1,
        accuracy: 68,
        attackSpeed: 2200,
        xp: 22,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },

    // ========== Bandit Hideout - New Mobs (Lv 20) ==========
    BANDIT_SCOUT: {
        id: 'bandit_scout',
        name: 'Bandit Scout',
        tier: 1,
        hp: 100,
        attack: 5,
        attackMax: 8,
        defense: 2,
        accuracy: 75,
        attackSpeed: 2600,
        xp: 36,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },
    BANDIT_SMUGGLER: {
        id: 'bandit_smuggler',
        name: 'Bandit Smuggler',
        tier: 1,
        hp: 120,
        attack: 4,
        attackMax: 7,
        defense: 4,
        accuracy: 65,
        attackSpeed: 3400,
        xp: 40,
        weakTo: 'magic',
        resistantTo: 'ranged'
    },
    BANDIT_HEXER: {
        id: 'bandit_hexer',
        name: 'Bandit Hexer',
        tier: 1,
        hp: 85,
        attack: 7,
        attackMax: 11,
        defense: 2,
        accuracy: 72,
        attackSpeed: 3600,
        xp: 44,
        weakTo: 'melee',
        resistantTo: 'magic'
    },

    // ========== Troll Caves - New Mobs (Lv 30) ==========
    FUNGAL_CRAWLER: {
        id: 'fungal_crawler',
        name: 'Fungal Crawler',
        tier: 2,
        hp: 200,
        attack: 8,
        attackMax: 12,
        defense: 5,
        accuracy: 60,
        attackSpeed: 3600,
        xp: 62,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    STONE_GOLEM: {
        id: 'stone_golem',
        name: 'Stone Golem',
        tier: 2,
        hp: 320,
        attack: 6,
        attackMax: 10,
        defense: 14,
        accuracy: 55,
        attackSpeed: 5000,
        xp: 70,
        weakTo: 'magic',
        resistantTo: 'melee'
    },
    CAVE_SPIDER: {
        id: 'cave_spider',
        name: 'Cave Spider',
        tier: 2,
        hp: 180,
        attack: 10,
        attackMax: 15,
        defense: 4,
        accuracy: 70,
        attackSpeed: 2600,
        xp: 65,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },

    // ========== Undead Crypt - New Mobs (Lv 40) ==========
    SPECTRAL_ARCHER: {
        id: 'spectral_archer',
        name: 'Spectral Archer',
        tier: 2,
        hp: 175,
        attack: 14,
        attackMax: 22,
        defense: 6,
        accuracy: 78,
        attackSpeed: 2800,
        xp: 84,
        weakTo: 'magic',
        resistantTo: 'ranged'
    },
    BONE_COLLECTOR: {
        id: 'bone_collector',
        name: 'Bone Collector',
        tier: 2,
        hp: 250,
        attack: 12,
        attackMax: 18,
        defense: 10,
        accuracy: 62,
        attackSpeed: 3800,
        xp: 78,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    CURSED_KNIGHT: {
        id: 'cursed_knight',
        name: 'Cursed Knight',
        tier: 2,
        hp: 300,
        attack: 16,
        attackMax: 24,
        defense: 16,
        accuracy: 74,
        attackSpeed: 3600,
        xp: 90,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },

    // ========== Demon Scar - New Mobs (Lv 50) ==========
    PIT_FIEND: {
        id: 'pit_fiend',
        name: 'Pit Fiend',
        tier: 2,
        hp: 360,
        attack: 24,
        attackMax: 36,
        defense: 14,
        accuracy: 74,
        attackSpeed: 3200,
        xp: 105,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },
    SOUL_HARVESTER: {
        id: 'soul_harvester',
        name: 'Soul Harvester',
        tier: 2,
        hp: 300,
        attack: 18,
        attackMax: 28,
        defense: 10,
        accuracy: 76,
        attackSpeed: 3400,
        xp: 98,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    HELLHOUND: {
        id: 'hellhound',
        name: 'Hellhound',
        tier: 2,
        hp: 280,
        attack: 22,
        attackMax: 32,
        defense: 8,
        accuracy: 78,
        attackSpeed: 2400,
        xp: 100,
        weakTo: 'magic',
        resistantTo: 'melee'
    },

    // ========== Dragon Lair - New Mobs (Lv 60) ==========
    DRAKE_WHELP: {
        id: 'drake_whelp',
        name: 'Drake Whelp',
        tier: 3,
        hp: 480,
        attack: 32,
        attackMax: 44,
        defense: 18,
        accuracy: 76,
        attackSpeed: 2800,
        xp: 155,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    DRAGON_PRIEST: {
        id: 'dragon_priest',
        name: 'Dragon Priest',
        tier: 3,
        hp: 560,
        attack: 38,
        attackMax: 52,
        defense: 20,
        accuracy: 80,
        attackSpeed: 3600,
        xp: 180,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    SCALED_MARAUDER: {
        id: 'scaled_marauder',
        name: 'Scaled Marauder',
        tier: 3,
        hp: 620,
        attack: 40,
        attackMax: 56,
        defense: 26,
        accuracy: 78,
        attackSpeed: 3400,
        xp: 175,
        weakTo: 'magic',
        resistantTo: 'ranged'
    },

    // ========== Void Rift - New Mobs (Lv 75) ==========
    VOID_STALKER: {
        id: 'void_stalker',
        name: 'Void Stalker',
        tier: 3,
        hp: 700,
        attack: 46,
        attackMax: 62,
        defense: 22,
        accuracy: 82,
        attackSpeed: 2800,
        xp: 250,
        weakTo: 'ranged',
        resistantTo: 'magic'
    },
    RIFT_SCAVENGER: {
        id: 'rift_scavenger',
        name: 'Rift Scavenger',
        tier: 3,
        hp: 680,
        attack: 42,
        attackMax: 58,
        defense: 26,
        accuracy: 78,
        attackSpeed: 3400,
        xp: 245,
        weakTo: 'melee',
        resistantTo: 'ranged'
    },
    ENTROPY_MAW: {
        id: 'entropy_maw',
        name: 'Entropy Maw',
        tier: 3,
        hp: 900,
        attack: 50,
        attackMax: 66,
        defense: 30,
        accuracy: 80,
        attackSpeed: 3800,
        xp: 270,
        weakTo: 'magic',
        resistantTo: 'melee'
    },

    // ========== Ancient Sanctum - New Mobs (Lv 80) ==========
    SANCTUM_GUARDIAN: {
        id: 'sanctum_guardian',
        name: 'Sanctum Guardian',
        tier: 3,
        hp: 1080,
        attack: 52,
        attackMax: 68,
        defense: 38,
        accuracy: 82,
        attackSpeed: 3800,
        xp: 340,
        weakTo: 'ranged',
        resistantTo: 'melee'
    },
    ANCIENT_SCRIBE: {
        id: 'ancient_scribe',
        name: 'Ancient Scribe',
        tier: 3,
        hp: 900,
        attack: 48,
        attackMax: 64,
        defense: 28,
        accuracy: 84,
        attackSpeed: 3200,
        xp: 330,
        weakTo: 'melee',
        resistantTo: 'magic'
    },
    TEMPORAL_SHADE: {
        id: 'temporal_shade',
        name: 'Temporal Shade',
        tier: 3,
        hp: 1000,
        attack: 56,
        attackMax: 72,
        defense: 32,
        accuracy: 86,
        attackSpeed: 2600,
        xp: 360,
        weakTo: 'magic',
        resistantTo: 'ranged'
    },
    RELIC_HOARDER: {
        id: 'relic_hoarder',
        name: 'Relic Hoarder',
        tier: 3,
        hp: 1200,
        attack: 50,
        attackMax: 66,
        defense: 42,
        accuracy: 80,
        attackSpeed: 4200,
        xp: 350,
        weakTo: 'ranged',
        resistantTo: 'magic'
    }
};

// ==================== ZONE LOOT TABLES ====================
// Each zone has common and rare drops

export const ZONE_LOOT = {
    // Tier 1 Zones - Basic materials, herbs, seeds, fish
    goblin_camp: {
        common: [
            { itemId: 'coins', minQty: 4, maxQty: 15, chance: 0.85 },
            { itemId: 'potato', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.06 }
        ]
    },
    forgotten_outskirts: {
        common: [
            { itemId: 'coins', minQty: 6, maxQty: 20, chance: 0.88 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'raw_silver_sprat', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'onion_seed', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 1, chance: 0.05 }
        ]
    },
    bandit_hideout: {
        common: [
            { itemId: 'coins', minQty: 12, maxQty: 35, chance: 0.9 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arrow_shafts', minQty: 3, maxQty: 8, chance: 0.15 },
            { itemId: 'pyrestone_arrows', minQty: 2, maxQty: 6, chance: 0.1 },
            { itemId: 'cabbage', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // Tier 2 Zones - Bones, ichor, mid-tier materials
    troll_caves: {
        common: [
            { itemId: 'coins', minQty: 25, maxQty: 60, chance: 0.9 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'deepcoal', minQty: 1, maxQty: 2, chance: 0.06 }
        ]
    },
    undead_crypt: {
        common: [
            { itemId: 'coins', minQty: 28, maxQty: 70, chance: 0.88 },
            { itemId: 'small_bone', minQty: 1, maxQty: 3, chance: 0.35 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.3 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },
    demon_scar: {
        common: [
            { itemId: 'coins', minQty: 45, maxQty: 110, chance: 0.9 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'ashes', minQty: 3, maxQty: 6, chance: 0.3 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // Tier 3 Zones - High-tier bones, ichor, valuable materials
    dragon_lair: {
        common: [
            { itemId: 'coins', minQty: 90, maxQty: 240, chance: 0.95 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },
    void_rift: {
        common: [
            { itemId: 'coins', minQty: 140, maxQty: 360, chance: 0.95 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0002 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },
    ancient_sanctum: {
        common: [
            { itemId: 'coins', minQty: 220, maxQty: 550, chance: 0.95 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 3, chance: 0.05 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0005 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },

    // Tier 4 - The Ashen Crucible (shared zone loot for all tiers)
    ashen_crucible: {
        common: [
            { itemId: 'coins', minQty: 350, maxQty: 1200, chance: 0.95 },
            { itemId: 'ashes', minQty: 6, maxQty: 20, chance: 0.55 },
            { itemId: 'charred_bone', minQty: 2, maxQty: 5, chance: 0.35 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 4, chance: 0.22 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 2, maxQty: 6, chance: 0.25 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.001 }
        ]
    }
};

// ==================== PER-MOB LOOT TABLES ====================
// Each mob has unique drops based on their theme and tier.
// Design: Combat provides SUPPLEMENTARY materials (not replacement for gathering)
// Each mob has 5-12 possible drops with thematic variety.

export const MOB_LOOT = {
    // ========== Tier 1 - Goblin Camp (Lv 1) ==========
    // Goblins are thieves - drop stolen goods, herbs, seeds, common supplies
    goblin_raider: {
        common: [
            { itemId: 'coins', minQty: 3, maxQty: 12, chance: 0.85 },
            { itemId: 'potato', minQty: 1, maxQty: 3, chance: 0.25 },
            { itemId: 'onion', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.15 }
        ],
        rare: [
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'pyrestone_dirk', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'treated_gloves', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    goblin_shaman: {
        common: [
            { itemId: 'coins', minQty: 5, maxQty: 15, chance: 0.85 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'greenleaf_seed', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.15 }
        ],
        rare: [
            { itemId: 'arcane_residue', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'duskleaf_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'steady_brew', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'shamans_focus', minQty: 1, maxQty: 1, chance: 0.001 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'common_staff', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'linenweave_helm', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    goblin_skirmisher: {
        common: [
            { itemId: 'coins', minQty: 4, maxQty: 14, chance: 0.85 },
            { itemId: 'arrow_shafts', minQty: 3, maxQty: 8, chance: 0.2 },
            { itemId: 'pyrestone_arrows', minQty: 2, maxQty: 6, chance: 0.15 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'cooked_sand_smelt', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'softbark_logs', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'shortbow', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'guard_tonic', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'onion_seed', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'feathers', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'pyrestone_greatsword', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'treated_helm', minQty: 1, maxQty: 1, chance: 0.012 }
        ]
    },

    // ========== Tier 1 - Forgotten Outskirts (Lv 10) ==========
    // Scavengers - drop salvaged supplies, food, fish, random junk
    scavenger: {
        common: [
            { itemId: 'coins', minQty: 5, maxQty: 18, chance: 0.85 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'raw_silver_sprat', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'potato', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'cooked_sand_smelt', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'onion_seed', minQty: 1, maxQty: 3, chance: 0.06 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'quickening_draught', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'pyrestone_dirk', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'ironwood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'treated_boots', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'linenweave_gloves', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    stray_mercenary: {
        common: [
            { itemId: 'coins', minQty: 10, maxQty: 28, chance: 0.9 },
            { itemId: 'pyrestone_arrows', minQty: 3, maxQty: 8, chance: 0.2 },
            { itemId: 'cooked_silver_sprat', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cabbage', minQty: 1, maxQty: 3, chance: 0.15 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'pyrestone_dirk', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'pyrestone_sword', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'ironwood_staff', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'treated_legguards', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'treated_shoulders', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },
    feral_beast: {
        common: [
            { itemId: 'coins', minQty: 4, maxQty: 15, chance: 0.75 },
            { itemId: 'raw_silver_sprat', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'raw_blueback', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'steady_brew', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'bloodfang_pendant', minQty: 1, maxQty: 1, chance: 0.00125 },
            { itemId: 'ironwood_glyph_core', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'pyrestone_sword', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'treated_chestplate', minQty: 1, maxQty: 1, chance: 0.012 }
        ]
    },

    // ========== Tier 1 - Bandit Hideout (Lv 20) ==========
    // Bandits - drop stolen weapons, armor, logs, ores, supplies
    bandit_thug: {
        common: [
            { itemId: 'coins', minQty: 15, maxQty: 40, chance: 0.9 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'cooked_blueback', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cabbage', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        rare: [
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'pyrestone_sword', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'tomato_seed', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'pale_shard_dirk', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'pyrestone_helm', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'pyrestone_boots', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },
    bandit_archer: {
        common: [
            { itemId: 'coins', minQty: 12, maxQty: 35, chance: 0.88 },
            { itemId: 'arrow_shafts', minQty: 5, maxQty: 15, chance: 0.25 },
            { itemId: 'pyrestone_arrows', minQty: 5, maxQty: 12, chance: 0.2 },
            { itemId: 'blackiron_arrows', minQty: 2, maxQty: 5, chance: 0.1 },
            { itemId: 'softbark_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'raw_blueback', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'ironwood_shortbow', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ironwood_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'steady_brew', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'feathers', minQty: 2, maxQty: 5, chance: 0.15 },
            { itemId: 'softbark_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'green_wyrm_helm', minQty: 1, maxQty: 1, chance: 0.012 },
            { itemId: 'softbark_shortbow', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },
    bandit_enforcer: {
        common: [
            { itemId: 'coins', minQty: 20, maxQty: 50, chance: 0.92 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'tomato', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'cooked_silver_sprat', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'guard_tonic', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'quickening_draught', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'tomato_seed', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'softbark_glyph_core', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'pale_shard_greatsword', minQty: 1, maxQty: 1, chance: 0.012 },
            { itemId: 'pale_shard_helm', minQty: 1, maxQty: 1, chance: 0.012 },
            { itemId: 'silkweave_chestplate', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'pale_shard_sword', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'pale_shard_chestplate', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'green_wyrm_boots', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },

    // ========== Tier 2 - Troll Caves (Lv 30) ==========
    // Trolls live in caves - drop ores, bones, cave moss herbs, stolen supplies
    cave_troll: {
        common: [
            { itemId: 'coins', minQty: 25, maxQty: 65, chance: 0.9 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 3, chance: 0.25 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'raw_river_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'goldleaf_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'deepcoal', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'blackiron_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'corn_seed', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'goldleaf_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'blackiron_sword', minQty: 1, maxQty: 1, chance: 0.012 },
            { itemId: 'blue_wyrm_chestplate', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'softbark_staff', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blackiron_dirk', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blackiron_helm', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blue_wyrm_gloves', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'aetherweave_helm', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },
    troll_brute: {
        common: [
            { itemId: 'coins', minQty: 30, maxQty: 75, chance: 0.88 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'raw_river_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'cooked_river_char', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'blackiron_arrows', minQty: 3, maxQty: 8, chance: 0.1 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        rare: [
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'blackiron_bar', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'goldleaf_logs', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'berserkers_tooth', minQty: 1, maxQty: 1, chance: 0.000667 },
            { itemId: 'corn_seed', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'pale_shard_greatsword', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blue_wyrm_boots', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'aetherweave_boots', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },
    regenerating_troll: {
        common: [
            { itemId: 'coins', minQty: 28, maxQty: 70, chance: 0.9 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 3, chance: 0.22 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'sanctum_leaf_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'vigor_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'goldleaf_glyph_core', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'goldleaf_shortbow', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'aetherweave_chestplate', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },

    // ========== Tier 2 - Undead Crypt (Lv 40) ==========
    // Undead - drop bones, ashes, cloth, burial goods, ichor
    skeleton_warrior: {
        common: [
            { itemId: 'coins', minQty: 30, maxQty: 75, chance: 0.88 },
            { itemId: 'small_bone', minQty: 2, maxQty: 4, chance: 0.4 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.3 },
            { itemId: 'blackiron_arrows', minQty: 3, maxQty: 8, chance: 0.12 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        rare: [
            { itemId: 'blackiron_sword', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'strawberry_seed', minQty: 1, maxQty: 2, chance: 0.035 },
            { itemId: 'blackiron_greatsword', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blackiron_chestplate', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blackiron_legguards', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blue_wyrm_shoulders', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'aetherweave_gloves', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },
    rotting_zombie: {
        common: [
            { itemId: 'coins', minQty: 25, maxQty: 65, chance: 0.82 },
            { itemId: 'ashes', minQty: 3, maxQty: 6, chance: 0.35 },
            { itemId: 'small_bone', minQty: 1, maxQty: 3, chance: 0.3 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.1 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'raw_crimson_char', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'deepvine_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'redgrain_glyph_core', minQty: 1, maxQty: 1, chance: 0.018 },
            { itemId: 'blackiron_greatsword', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blue_wyrm_legguards', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },
    crypt_acolyte: {
        common: [
            { itemId: 'coins', minQty: 35, maxQty: 85, chance: 0.88 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 4, chance: 0.28 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.25 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'redgrain_logs', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'sunpetal_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'hardened_infusion', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'strawberry_seed', minQty: 1, maxQty: 2, chance: 0.035 },
            { itemId: 'goldleaf_staff', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'skyiron_dirk', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'aetherweave_legguards', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'blue_wyrm_helm', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },

    // ========== Tier 2 - Demon Scar (Lv 50) ==========
    // Demons - drop charred materials, high-tier herbs, ichor, fire-touched items
    lesser_demon: {
        common: [
            { itemId: 'coins', minQty: 45, maxQty: 110, chance: 0.9 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 3, chance: 0.3 },
            { itemId: 'ashes', minQty: 3, maxQty: 7, chance: 0.35 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cooked_crimson_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'redgrain_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'watermelon_seed', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'skyiron_dirk', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'skyiron_greatsword', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'skyiron_helm', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'red_wyrm_boots', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'runebound_gloves', minQty: 1, maxQty: 1, chance: 0.006 }
        ]
    },
    flame_imp: {
        common: [
            { itemId: 'coins', minQty: 40, maxQty: 100, chance: 0.88 },
            { itemId: 'ashes', minQty: 4, maxQty: 10, chance: 0.45 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'raw_crimson_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'skyiron_arrows', minQty: 2, maxQty: 6, chance: 0.1 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.1 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'redgrain_logs', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'stonebark_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'redgrain_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'goldleaf_staff', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'runebound_helm', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },
    scarred_cultist: {
        common: [
            { itemId: 'coins', minQty: 50, maxQty: 120, chance: 0.9 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 3, chance: 0.22 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'skyiron_arrows', minQty: 3, maxQty: 8, chance: 0.1 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        rare: [
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.07 },
            { itemId: 'deepvine_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'veiled_draught', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'scorched_talisman', minQty: 1, maxQty: 1, chance: 0.000667 },
            { itemId: 'watermelon_seed', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'skyiron_sword', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'redgrain_staff', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'runebound_boots', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'red_wyrm_legguards', minQty: 1, maxQty: 1, chance: 0.006 }
        ]
    },

    // ========== Tier 3 - Dragon Lair (Lv 60) ==========
    // Dragons - drop high-tier hides, bones, ores, treasure hoards
    wyrmling: {
        common: [
            { itemId: 'coins', minQty: 90, maxQty: 220, chance: 0.95 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 3, chance: 0.85 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'raw_broadbill', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'verdant_core_ore', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'brightmaw_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'starbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'gravewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'skyiron_sword', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'red_wyrm_chestplate', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'runebound_chestplate', minQty: 1, maxQty: 1, chance: 0.006 }
        ]
    },
    ancient_dragon: {
        common: [
            { itemId: 'coins', minQty: 120, maxQty: 300, chance: 0.95 },
            { itemId: 'red_wyrm_hide', minQty: 2, maxQty: 4, chance: 0.90 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 3, chance: 0.28 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cooked_broadbill', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'verdant_core_arrows', minQty: 3, maxQty: 8, chance: 0.1 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'gravewood_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'starbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'sharpshot_brew', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'dragons_heart', minQty: 1, maxQty: 1, chance: 0.0005 },
            { itemId: 'gravewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.018 },
            { itemId: 'verdant_core_greatsword', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'redgrain_shortbow', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'black_drake_chestplate', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'shadowweave_chestplate', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },
    draconic_sentinel: {
        common: [
            { itemId: 'coins', minQty: 100, maxQty: 260, chance: 0.95 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 4, chance: 0.22 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'verdant_core_arrows', minQty: 5, maxQty: 12, chance: 0.12 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.85 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.1 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'verdant_core_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'verdant_core_ore', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'gravewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'redgrain_staff', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'verdant_core_sword', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'red_wyrm_helm', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'runebound_helm', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'verdant_core_dirk', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'verdant_core_helm', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'verdant_core_boots', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'red_wyrm_gloves', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'runebound_legguards', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },

    // ========== Tier 3 - Void Rift (Lv 75) ==========
    // Void creatures - drop void materials, rare ichor, otherworldly items
    void_aberration: {
        common: [
            { itemId: 'coins', minQty: 140, maxQty: 340, chance: 0.95 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 3, chance: 0.85 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'glowstem_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'voidsteel_sword', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'gravewood_shortbow', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'black_drake_helm', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'shadowweave_helm', minQty: 1, maxQty: 1, chance: 0.004 }
        ]
    },
    rift_horror: {
        common: [
            { itemId: 'coins', minQty: 160, maxQty: 400, chance: 0.95 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.22 },
            { itemId: 'void_bone', minQty: 1, maxQty: 3, chance: 0.25 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.85 },
            { itemId: 'raw_broadbill', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'voidsteel_arrows', minQty: 3, maxQty: 8, chance: 0.12 },
            { itemId: 'cooked_mako', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.15 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'sharpshot_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'ashbloom_seed', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.018 },
            { itemId: 'voidsteel_greatsword', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'gravewood_staff', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'void_drake_chestplate', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'voidweave_chestplate', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },
    void_channeler: {
        common: [
            { itemId: 'coins', minQty: 150, maxQty: 380, chance: 0.95 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.85 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.1 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0001 },
            { itemId: 'nightvine_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'arcanewood_seed', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'veiled_draught', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'void_siphon', minQty: 1, maxQty: 1, chance: 0.0004 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'voidsteel_dirk', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'arcanewood_shortbow', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'void_drake_helm', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'shadowweave_chestplate', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },

    // ========== Tier 3 - Ancient Sanctum (Lv 90) ==========
    // Ancient beings - drop elder bones, rare crafting materials, powerful items
    elder_construct: {
        common: [
            { itemId: 'coins', minQty: 220, maxQty: 550, chance: 0.95 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.85 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'cooked_broadbill', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'hardened_infusion', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'kings_fruit_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'voidsteel_sword', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'arcanewood_shortbow', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'void_drake_legguards', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'voidweave_helm', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },
    corrupted_avatar: {
        common: [
            { itemId: 'coins', minQty: 260, maxQty: 620, chance: 0.95 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.25 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 3, chance: 0.25 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.85 },
            { itemId: 'voidsteel_arrows', minQty: 5, maxQty: 12, chance: 0.12 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'vial_of_water', minQty: 2, maxQty: 5, chance: 0.1 },
            { itemId: 'voidweave', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 3, chance: 0.08 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'ashbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'starbloom_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.008 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.022 },
            { itemId: 'voidsteel_greatsword', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'arcanewood_staff', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'void_drake_chestplate', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'voidweave_chestplate', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },
    elder_god_fragment: {
        common: [
            { itemId: 'coins', minQty: 300, maxQty: 750, chance: 0.95 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 3, chance: 0.90 },
            { itemId: 'elder_bone', minQty: 2, maxQty: 4, chance: 0.28 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.22 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'cooked_mako', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'voidweave', minQty: 1, maxQty: 2, chance: 0.15 }
        ],
        rare: [
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0006 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'crownwort_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'kings_fruit_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'ascendant_concoction', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'elder_sigil', minQty: 1, maxQty: 1, chance: 0.000333 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'voidsteel_dirk', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'gravewood_shortbow', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'void_drake_helm', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'voidweave_legguards', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },

    // ========== Themed Mobs — Target Farming ==========
    // Specialty drops use REVISED rates (lower than plan originals, balanced for idle AFK)

    // Goblin Herbalist — Goblin Camp: herbs + seeds
    goblin_herbalist: {
        common: [
            { itemId: 'coins', minQty: 3, maxQty: 10, chance: 0.82 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'greenleaf_seed', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'potato', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'duskleaf_seed', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },

    // Camp Chicken — Goblin Camp: feathers + bones (early arrow crafting material)
    camp_chicken: {
        common: [
            { itemId: 'feathers', minQty: 2, maxQty: 4, chance: 1.0 },
            { itemId: 'small_bone', minQty: 1, maxQty: 1, chance: 0.60 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'onion_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 1, chance: 0.05 }
        ],
        rare: [
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },

    // Carrion Hawk — Forgotten Outskirts: feathers + raw fish
    carrion_hawk: {
        common: [
            { itemId: 'coins', minQty: 5, maxQty: 16, chance: 0.82 },
            { itemId: 'feathers', minQty: 2, maxQty: 5, chance: 0.20 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'raw_silver_sprat', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arrow_shafts', minQty: 2, maxQty: 6, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 1, chance: 0.1 }
        ],
        rare: [
            { itemId: 'onion_seed', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'raw_blueback', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // Bandit Alchemist — Bandit Hideout: potions + mid herbs + vials
    bandit_alchemist: {
        common: [
            { itemId: 'coins', minQty: 15, maxQty: 40, chance: 0.88 },
            { itemId: 'vial_of_water', minQty: 2, maxQty: 5, chance: 0.25 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 3, chance: 0.2 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cooked_silver_sprat', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'guard_tonic', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'steady_brew', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'quickening_draught', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'emberroot_seed', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },

    // Crystal Lurker — Troll Caves: gems + ores (REVISED rates)
    crystal_lurker: {
        common: [
            { itemId: 'coins', minQty: 30, maxQty: 80, chance: 0.9 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'deepcoal', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'cooked_river_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'blackiron_arrows', minQty: 3, maxQty: 8, chance: 0.12 }
        ],
        rare: [
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // Tomb Weaver — Undead Crypt: cloth + low glyphs (REVISED rates)
    tomb_weaver: {
        common: [
            { itemId: 'coins', minQty: 35, maxQty: 85, chance: 0.88 },
            { itemId: 'spun_fiber', minQty: 2, maxQty: 4, chance: 0.15 },
            { itemId: 'linenweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.25 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'vigor_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'sanctum_leaf_seed', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },

    // Infernal Arcanist — Demon Scar: glyphs + ichor (REVISED rates)
    infernal_arcanist: {
        common: [
            { itemId: 'coins', minQty: 50, maxQty: 130, chance: 0.9 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'ashes', minQty: 3, maxQty: 8, chance: 0.35 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'skyiron_arrows', minQty: 3, maxQty: 6, chance: 0.1 },
            { itemId: 'cooked_crimson_char', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'vigor_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // Hoard Guardian — Dragon Lair: high gems + gold/bars (REVISED rates)
    hoard_guardian: {
        common: [
            { itemId: 'coins', minQty: 150, maxQty: 400, chance: 0.95 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'cooked_broadbill', minQty: 1, maxQty: 2, chance: 0.08 }
        ],
        rare: [
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'verdant_core_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'verdant_core_ore', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'gravewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.018 },
            { itemId: 'verdant_core_sword', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'black_drake_boots', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },

    // Rift Weaver — Void Rift: high cloth + high glyphs (REVISED rates)
    rift_weaver: {
        common: [
            { itemId: 'coins', minQty: 160, maxQty: 380, chance: 0.95 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'voidsteel_arrows', minQty: 3, maxQty: 8, chance: 0.12 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'sharpshot_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'nightvine_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'arcanewood_glyph_core', minQty: 1, maxQty: 1, chance: 0.018 },
            { itemId: 'gravewood_staff', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'shadowweave_legguards', minQty: 1, maxQty: 1, chance: 0.004 }
        ]
    },

    // Elder Herbkeeper — Ancient Sanctum: endgame herbs + potions (REVISED rates)
    elder_herbkeeper: {
        common: [
            { itemId: 'coins', minQty: 250, maxQty: 600, chance: 0.95 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'vial_of_water', minQty: 2, maxQty: 5, chance: 0.15 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.2 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.08 }
        ],
        rare: [
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'ascendant_concoction', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'crownwort_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'ashbloom_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },

    // ========== Pyre Veil Hidden Mobs ==========
    // These have enhanced drops + unique veil_ember drops
    ember_goblin: {
        common: [
            { itemId: 'coins', minQty: 10, maxQty: 28, chance: 0.88 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.4 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'common_timber', minQty: 1, maxQty: 3, chance: 0.12 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'potato', minQty: 1, maxQty: 3, chance: 0.12 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'emberroot_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    black_market_runner: {
        common: [
            { itemId: 'coins', minQty: 25, maxQty: 60, chance: 0.9 },
            { itemId: 'ashes', minQty: 2, maxQty: 6, chance: 0.35 },
            { itemId: 'blackiron_arrows', minQty: 5, maxQty: 12, chance: 0.15 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'cooked_blueback', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'softbark_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'guard_tonic', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    ashbound_troll: {
        common: [
            { itemId: 'coins', minQty: 40, maxQty: 95, chance: 0.9 },
            { itemId: 'ashes', minQty: 4, maxQty: 8, chance: 0.45 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 3, chance: 0.28 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'raw_river_char', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'goldleaf_logs', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 1, chance: 0.1 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 2, chance: 0.14 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.1 },
            { itemId: 'goldleaf_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'blackiron_bar', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    scorched_revenant: {
        common: [
            { itemId: 'coins', minQty: 55, maxQty: 130, chance: 0.9 },
            { itemId: 'ashes', minQty: 5, maxQty: 12, chance: 0.5 },
            { itemId: 'charred_bone', minQty: 2, maxQty: 4, chance: 0.32 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.1 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 3, chance: 0.16 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'redgrain_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    cinderbound_cultist: {
        common: [
            { itemId: 'coins', minQty: 70, maxQty: 170, chance: 0.92 },
            { itemId: 'ashes', minQty: 5, maxQty: 14, chance: 0.55 },
            { itemId: 'charred_bone', minQty: 2, maxQty: 5, chance: 0.35 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'redgrain_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'skyiron_arrows', minQty: 3, maxQty: 8, chance: 0.1 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.1 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 2, maxQty: 4, chance: 0.2 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'gravewood_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'veiled_draught', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    molten_wyrm: {
        common: [
            { itemId: 'coins', minQty: 120, maxQty: 290, chance: 0.95 },
            { itemId: 'ashes', minQty: 6, maxQty: 16, chance: 0.5 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 3, chance: 0.28 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 2, maxQty: 4, chance: 0.22 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'sharpshot_brew', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'verdant_core_ore', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'skyiron_sword', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'redgrain_shortbow', minQty: 1, maxQty: 1, chance: 0.006 },
            { itemId: 'red_wyrm_shoulders', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'shadowweave_boots', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },
    void_ash_horror: {
        common: [
            { itemId: 'coins', minQty: 180, maxQty: 420, chance: 0.95 },
            { itemId: 'ashes', minQty: 8, maxQty: 20, chance: 0.55 },
            { itemId: 'void_bone', minQty: 1, maxQty: 4, chance: 0.32 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'veil_ember', minQty: 3, maxQty: 5, chance: 0.25 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.1 },
            { itemId: 'arcanewood_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'voidsteel_greatsword', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'arcanewood_staff', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'void_drake_boots', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'voidweave_gloves', minQty: 1, maxQty: 1, chance: 0.003 }
        ]
    },

    // ========== Cindered Reliquary Dungeon Bosses ==========
    // Loot tables from combat-overhaul-plan: gear drops + material farming returns
    ashen_warden: {
        common: [
            { itemId: 'coins', minQty: 500, maxQty: 1200, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 2, maxQty: 4, chance: 0.35 },
            { itemId: 'dragon_bone', minQty: 2, maxQty: 3, chance: 0.30 },
            { itemId: 'ashes', minQty: 5, maxQty: 12, chance: 0.40 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'cooked_broadbill', minQty: 2, maxQty: 3, chance: 0.15 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'ashen_greatsword', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'wardens_ember_ring', minQty: 1, maxQty: 1, chance: 0.013 },
            { itemId: 'emberheart_focus', minQty: 1, maxQty: 1, chance: 0.013 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'gravewood_seed', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },
    pyre_guardian: {
        common: [
            { itemId: 'coins', minQty: 800, maxQty: 1800, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 3, maxQty: 5, chance: 0.40 },
            { itemId: 'void_bone', minQty: 2, maxQty: 4, chance: 0.30 },
            { itemId: 'ashes', minQty: 8, maxQty: 18, chance: 0.45 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'arcanewood_logs', minQty: 2, maxQty: 3, chance: 0.18 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 3, chance: 0.15 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'cooked_broadbill', minQty: 2, maxQty: 4, chance: 0.14 }
        ],
        rare: [
            { itemId: 'pyreguard_blade', minQty: 1, maxQty: 1, chance: 0.017 },
            { itemId: 'pyrebound_staff', minQty: 1, maxQty: 1, chance: 0.017 },
            { itemId: 'ashen_bulwark', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'guardians_flame_signet', minQty: 1, maxQty: 1, chance: 0.0125 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ascendant_concoction', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.07 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'gravewood_seed', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    flame_eternal: {
        common: [
            { itemId: 'coins', minQty: 1200, maxQty: 3000, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 4, maxQty: 8, chance: 0.50 },
            { itemId: 'elder_bone', minQty: 2, maxQty: 4, chance: 0.30 },
            { itemId: 'ashes', minQty: 10, maxQty: 25, chance: 0.50 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 3, chance: 0.20 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arcanewood_logs', minQty: 2, maxQty: 4, chance: 0.20 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 3, chance: 0.15 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 2, chance: 0.14 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'cindered_shortbow', minQty: 1, maxQty: 1, chance: 0.013 },
            { itemId: 'eternal_inferno_band', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'cindered_quiver', minQty: 1, maxQty: 1, chance: 0.0125 },
            { itemId: 'ember_ward', minQty: 1, maxQty: 1, chance: 0.004 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'arcanewood_seed', minQty: 1, maxQty: 1, chance: 0.025 }
        ]
    },

    // ========== Set Bosses (The Ashen Crucible — Tier 2) ==========
    // Shared material pool + unique set armor drops
    // Inverse triangle: each drops gear for a DIFFERENT combat style
    searing_matriarch: {
        common: [
            { itemId: 'coins', minQty: 2000, maxQty: 5000, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 6, maxQty: 10, chance: 0.60 },
            { itemId: 'elder_bone', minQty: 3, maxQty: 5, chance: 0.35 },
            { itemId: 'ashes', minQty: 15, maxQty: 30, chance: 0.50 },
            { itemId: 'voidsteel_bar', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.18 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'gravewood_logs', minQty: 2, maxQty: 4, chance: 0.16 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'cooked_broadbill', minQty: 2, maxQty: 4, chance: 0.12 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'watermelon', minQty: 2, maxQty: 4, chance: 0.12 }
        ],
        rare: [
            { itemId: 'dormant_pyreweave_head', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_pyreweave_chestplate', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_pyreweave_legguards', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_pyreweave_boots', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_pyreweave_gloves', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_pyreweave_shoulders', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'universal_set_token', minQty: 1, maxQty: 1, chance: 0.0011 },
            { itemId: 'crownwort_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'arcane_tincture', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'faint_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'smoldering_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'kindled_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'blazing_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'veil_touched_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'convergent_insight', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },
    molten_colossus: {
        common: [
            { itemId: 'coins', minQty: 2000, maxQty: 5000, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 6, maxQty: 10, chance: 0.60 },
            { itemId: 'void_bone', minQty: 3, maxQty: 5, chance: 0.35 },
            { itemId: 'ashes', minQty: 15, maxQty: 30, chance: 0.50 },
            { itemId: 'voidsteel_bar', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.18 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'arcanewood_logs', minQty: 2, maxQty: 4, chance: 0.16 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 3, chance: 0.14 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'starbloom', minQty: 2, maxQty: 4, chance: 0.12 }
        ],
        rare: [
            { itemId: 'dormant_cindermark_head', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_cindermark_chestplate', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_cindermark_legguards', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_cindermark_boots', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_cindermark_gloves', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_cindermark_shoulders', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'universal_set_token', minQty: 1, maxQty: 1, chance: 0.0011 },
            { itemId: 'kings_fruit_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'sharpshot_brew', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'faint_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'smoldering_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'kindled_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'blazing_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'veil_touched_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'convergent_insight', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },
    cindered_knight: {
        common: [
            { itemId: 'coins', minQty: 2000, maxQty: 5000, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 6, maxQty: 10, chance: 0.60 },
            { itemId: 'elder_bone', minQty: 3, maxQty: 5, chance: 0.35 },
            { itemId: 'ashes', minQty: 15, maxQty: 30, chance: 0.50 },
            { itemId: 'voidsteel_bar', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 3, chance: 0.20 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.18 },
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'gravewood_logs', minQty: 2, maxQty: 4, chance: 0.16 },
            { itemId: 'voidweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 2, chance: 0.14 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'kings_fruit', minQty: 1, maxQty: 3, chance: 0.12 }
        ],
        rare: [
            { itemId: 'dormant_ashforged_head', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_ashforged_chestplate', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_ashforged_legguards', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_ashforged_boots', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_ashforged_gloves', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'dormant_ashforged_shoulders', minQty: 1, maxQty: 1, chance: 0.0033 },
            { itemId: 'universal_set_token', minQty: 1, maxQty: 1, chance: 0.0011 },
            { itemId: 'starbloom_seed', minQty: 1, maxQty: 1, chance: 0.025 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.05 },
            { itemId: 'rallying_elixir', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'faint_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'smoldering_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'kindled_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'blazing_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'veil_touched_insight', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'convergent_insight', minQty: 1, maxQty: 1, chance: 0.005 }
        ]
    },

    // ========== Ashen Convergence Raid Boss ==========
    // Eclipse gear: weapons 1/500 each rolled from 4, aegis 1/500, rings 1/350 each rolled from 3
    convergence_herald: {
        common: [
            { itemId: 'coins', minQty: 5000, maxQty: 12000, chance: 0.95 },
            { itemId: 'veil_ember', minQty: 10, maxQty: 15, chance: 0.75 },
            { itemId: 'void_bone', minQty: 4, maxQty: 8, chance: 0.40 },
            { itemId: 'ashes', minQty: 20, maxQty: 40, chance: 0.55 },
            { itemId: 'voidsteel_bar', minQty: 2, maxQty: 4, chance: 0.25 },
            { itemId: 'void_drake_hide', minQty: 2, maxQty: 4, chance: 0.22 },
            { itemId: 'purified_arcane_core', minQty: 3, maxQty: 5, chance: 0.20 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 2, chance: 0.06 },
            { itemId: 'ascendant_concoction', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 2, chance: 0.05 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 2, chance: 0.08 },
            { itemId: 'gravewood_logs', minQty: 2, maxQty: 5, chance: 0.18 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 3, chance: 0.15 }
        ],
        rare: [
            // Eclipse Weapons — 1/500 total, rolled randomly (0.002 / 4 = 0.0005 each)
            { itemId: 'eclipse_reaver', minQty: 1, maxQty: 1, chance: 0.0005 },
            { itemId: 'eclipse_edge', minQty: 1, maxQty: 1, chance: 0.0005 },
            { itemId: 'eclipse_piercer', minQty: 1, maxQty: 1, chance: 0.0005 },
            { itemId: 'eclipse_conduit', minQty: 1, maxQty: 1, chance: 0.0005 },
            // Eclipse Aegis — 1/500
            { itemId: 'eclipse_aegis', minQty: 1, maxQty: 1, chance: 0.002 },
            // Eclipse Rings — 1/350 total, rolled randomly (0.0029 / 3 ≈ 0.000967 each)
            { itemId: 'eclipse_wrath_band', minQty: 1, maxQty: 1, chance: 0.000967 },
            { itemId: 'eclipse_precision_band', minQty: 1, maxQty: 1, chance: 0.000967 },
            { itemId: 'eclipse_arcana_band', minQty: 1, maxQty: 1, chance: 0.000967 },
            // Spirit Glyph + Onyx (from Phase 2f additions)
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'arcanewood_seed', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.035 },
            { itemId: 'faint_insight', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'smoldering_insight', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'kindled_insight', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blazing_insight', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'veil_touched_insight', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'convergent_insight', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },

    // ==================== NEW MOB LOOT - Combat Expansion ====================

    // ========== Goblin Camp - New Mobs (Lv 1) ==========
    goblin_trapper: {
        common: [
            { itemId: 'coins', minQty: 3, maxQty: 10, chance: 0.80 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'arrow_shafts', minQty: 2, maxQty: 5, chance: 0.20 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'small_bone', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'tomato_seed', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'treated_gloves', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    goblin_looter: {
        common: [
            { itemId: 'coins', minQty: 8, maxQty: 20, chance: 0.90 },
            { itemId: 'potato', minQty: 1, maxQty: 3, chance: 0.20 },
            { itemId: 'onion_seed', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'common_timber', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'small_bone', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'raw_silver_sprat', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'treated_helm', minQty: 1, maxQty: 1, chance: 0.012 }
        ]
    },
    goblin_firestarter: {
        common: [
            { itemId: 'coins', minQty: 3, maxQty: 8, chance: 0.75 },
            { itemId: 'ashes', minQty: 1, maxQty: 3, chance: 0.30 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.18 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'greenleaf_seed', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'feral_draft', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },

    // ========== Forgotten Outskirts - New Mobs (Lv 10) ==========
    ruined_sentry: {
        common: [
            { itemId: 'coins', minQty: 5, maxQty: 15, chance: 0.85 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arrow_shafts', minQty: 2, maxQty: 5, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.10 }
        ],
        rare: [
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'treated_chestplate', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },
    marsh_forager: {
        common: [
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'onion_seed', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'duskleaf_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.15 }
        ],
        rare: [
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.01 }
        ]
    },
    wild_hound: {
        common: [
            { itemId: 'coins', minQty: 5, maxQty: 12, chance: 0.80 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'raw_sand_smelt', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'feathers', minQty: 1, maxQty: 2, chance: 0.10 }
        ],
        rare: [
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'common_glyph_core', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'pyrestone_sword', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'ironwood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },

    // ========== Bandit Hideout - New Mobs (Lv 20) ==========
    bandit_scout: {
        common: [
            { itemId: 'coins', minQty: 10, maxQty: 25, chance: 0.88 },
            { itemId: 'arrow_shafts', minQty: 3, maxQty: 8, chance: 0.20 },
            { itemId: 'feathers', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'raw_blueback', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'tomato', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        rare: [
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'ironwood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ironwood_shortbow', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },
    bandit_smuggler: {
        common: [
            { itemId: 'coins', minQty: 12, maxQty: 30, chance: 0.88 },
            { itemId: 'pyrestone_ore', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'pyrestone_bar', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'softbark_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'cabbage', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'ironwood_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'pale_shard_dirk', minQty: 1, maxQty: 1, chance: 0.012 }
        ]
    },
    bandit_hexer: {
        common: [
            { itemId: 'coins', minQty: 8, maxQty: 22, chance: 0.82 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 1, chance: 0.18 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'ashes', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'linenweave', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_duskleaf', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'ironwood_logs', minQty: 1, maxQty: 2, chance: 0.04 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'softbark_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'pale_shard_bar', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'linenweave_helm', minQty: 1, maxQty: 1, chance: 0.012 }
        ]
    },

    // ========== Troll Caves - New Mobs (Lv 30) ==========
    fungal_crawler: {
        common: [
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'grimy_ironbloom', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'vial_of_water', minQty: 1, maxQty: 3, chance: 0.20 },
            { itemId: 'spun_fiber', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'emberroot_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'goldleaf_seed', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'raw_river_char', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'gold_ore', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'goldleaf_glyph_core', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    stone_golem: {
        common: [
            { itemId: 'coins', minQty: 15, maxQty: 40, chance: 0.85 },
            { itemId: 'pale_shard_ore', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'small_bone', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'deepcoal', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'goldleaf_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'blackiron_bar', minQty: 1, maxQty: 1, chance: 0.04 }
        ]
    },
    cave_spider: {
        common: [
            { itemId: 'spun_fiber', minQty: 1, maxQty: 3, chance: 0.25 },
            { itemId: 'treated_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_greenleaf', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'raw_river_char', minQty: 1, maxQty: 2, chance: 0.12 }
        ],
        rare: [
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.04 },
            { itemId: 'balanced_infusion', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },

    // ========== Undead Crypt - New Mobs (Lv 40) ==========
    spectral_archer: {
        common: [
            { itemId: 'coins', minQty: 25, maxQty: 50, chance: 0.85 },
            { itemId: 'arrow_shafts', minQty: 3, maxQty: 8, chance: 0.20 },
            { itemId: 'feathers', minQty: 1, maxQty: 3, chance: 0.18 },
            { itemId: 'ashes', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'redgrain_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    bone_collector: {
        common: [
            { itemId: 'small_bone', minQty: 2, maxQty: 4, chance: 0.30 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'ashes', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'grimy_sanctum_leaf', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'sanctum_leaf_seed', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'sunpetal_seed', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'gold_ore', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'focused_elixir', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'redgrain_glyph_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },
    cursed_knight: {
        common: [
            { itemId: 'coins', minQty: 30, maxQty: 60, chance: 0.88 },
            { itemId: 'blackiron_ore', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'blackiron_bar', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'silkweave', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'green_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'verdant_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'blackiron_greatsword', minQty: 1, maxQty: 1, chance: 0.008 }
        ]
    },

    // ========== Demon Scar - New Mobs (Lv 50) ==========
    pit_fiend: {
        common: [
            { itemId: 'coins', minQty: 40, maxQty: 80, chance: 0.88 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.22 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'ashes', minQty: 1, maxQty: 3, chance: 0.20 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.10 }
        ],
        rare: [
            { itemId: 'raw_crimson_char', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'sustaining_brew', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'hardened_infusion', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    soul_harvester: {
        common: [
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'arcane_residue', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'entropy_glyph', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'aetherweave', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'sunpetal_seed', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },
    hellhound: {
        common: [
            { itemId: 'coins', minQty: 40, maxQty: 75, chance: 0.85 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'ashes', minQty: 2, maxQty: 5, chance: 0.20 },
            { itemId: 'charred_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'blue_wyrm_hide', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'raw_skipjack', minQty: 1, maxQty: 2, chance: 0.10 }
        ],
        rare: [
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.003 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'hardened_infusion', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },

    // ========== Dragon Lair - New Mobs (Lv 60) ==========
    drake_whelp: {
        common: [
            { itemId: 'coins', minQty: 50, maxQty: 100, chance: 0.88 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'gold_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'raw_spiny_lobster', minQty: 1, maxQty: 2, chance: 0.08 }
        ],
        rare: [
            { itemId: 'grimy_sunpetal', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    dragon_priest: {
        common: [
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'refined_arcane_ichor', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'grimy_brightmaw', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'runebound_cloth', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'grimy_deepvine', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'redgrain_logs', minQty: 1, maxQty: 2, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    scaled_marauder: {
        common: [
            { itemId: 'coins', minQty: 50, maxQty: 150, chance: 0.90 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'skyiron_ore', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'gravewood_logs', minQty: 1, maxQty: 2, chance: 0.10 }
        ],
        rare: [
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.03 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'emerald', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 1, chance: 0.03 }
        ]
    },

    // ========== Void Rift - New Mobs (Lv 75) ==========
    void_stalker: {
        common: [
            { itemId: 'coins', minQty: 75, maxQty: 150, chance: 0.88 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.02 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    rift_scavenger: {
        common: [
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'verdant_core_ore', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'skyiron_bar', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'glowstem_seed', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'grimy_nightvine', minQty: 1, maxQty: 2, chance: 0.02 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },
    entropy_maw: {
        common: [
            { itemId: 'coins', minQty: 75, maxQty: 175, chance: 0.88 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.20 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'ashes', minQty: 2, maxQty: 4, chance: 0.18 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'raw_broadbill', minQty: 1, maxQty: 2, chance: 0.08 }
        ],
        rare: [
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'ruby', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.015 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.015 }
        ]
    },

    // ========== Ancient Sanctum - New Mobs (Lv 80) ==========
    sanctum_guardian: {
        common: [
            { itemId: 'coins', minQty: 100, maxQty: 300, chance: 0.90 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.18 },
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'raw_mako', minQty: 1, maxQty: 2, chance: 0.08 }
        ],
        rare: [
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 2, chance: 0.02 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    ancient_scribe: {
        common: [
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.12 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.06 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'arcanewood_logs', minQty: 1, maxQty: 2, chance: 0.10 },
            { itemId: 'crownwort_seed', minQty: 1, maxQty: 1, chance: 0.05 }
        ],
        rare: [
            { itemId: 'runebound_cloth', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0003 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    temporal_shade: {
        common: [
            { itemId: 'coins', minQty: 100, maxQty: 250, chance: 0.88 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'shadowweave', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 1, chance: 0.08 }
        ],
        rare: [
            { itemId: 'voidsteel_bar', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    },
    relic_hoarder: {
        common: [
            { itemId: 'coins', minQty: 300, maxQty: 700, chance: 0.92 },
            { itemId: 'gold_bar', minQty: 1, maxQty: 2, chance: 0.15 },
            { itemId: 'voidsteel_ore', minQty: 1, maxQty: 2, chance: 0.12 },
            { itemId: 'grimy_ashbloom', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'voidweave', minQty: 1, maxQty: 1, chance: 0.08 },
            { itemId: 'verdant_core_bar', minQty: 1, maxQty: 1, chance: 0.06 }
        ],
        rare: [
            { itemId: 'grimy_glowstem', minQty: 1, maxQty: 2, chance: 0.02 },
            { itemId: 'onyx', minQty: 1, maxQty: 1, chance: 0.005 },
            { itemId: 'astryx_stone', minQty: 1, maxQty: 1, chance: 0.01 },
            { itemId: 'diamond', minQty: 1, maxQty: 1, chance: 0.02 },
            { itemId: 'universal_glyph_catalyst', minQty: 1, maxQty: 1, chance: 0.0003 },
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 1, chance: 0.02 }
        ]
    }
};

// ==================== TIER CONFIGURATION ====================

export const COMBAT_TIERS = {
    1: {
        name: 'Wilderness',
        description: 'Untamed lands filled with common threats.',
        icon: '🏕️'
    },
    2: {
        name: 'Depths',
        description: 'Darker regions where danger lurks in shadow.',
        icon: '🕳️'
    },
    3: {
        name: 'Forbidden',
        description: 'Realms of ancient power and cosmic horror.',
        icon: '🌑'
    },
    4: {
        name: 'Pyre Veil',
        description: 'Hidden realms revealed only to masters of the flame.',
        icon: '🔥'
    },
    5: {
        name: 'Convergence',
        description: 'Where all flames return. Ultimate challenges await.',
        icon: '🌋'
    }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get a zone by ID
 * @param {string} zoneId - Zone ID
 * @returns {Object|null} Zone data
 */
export function getZone(zoneId) {
    return Object.values(COMBAT_ZONES).find(z => z.id === zoneId) || null;
}

/**
 * Get the mob list for a zone, supporting tiered zones.
 * For tiered zones (e.g. Ashen Crucible), returns mobs from the specified tier.
 * For normal zones, returns the zone's mobs array.
 * @param {string} zoneId - Zone ID
 * @param {string|null} tier - Tier key (e.g. 't1', 't2', 't3') for tiered zones
 * @returns {string[]} Array of mob IDs
 */
export function getZoneMobs(zoneId, tier = null) {
    const zone = getZone(zoneId);
    if (!zone) return [];

    // Tiered zone
    if (zone.tiers) {
        if (tier && zone.tiers[tier]) {
            return zone.tiers[tier].mobs || [];
        }
        // No tier specified — combine all tier mobs
        let allMobs = [];
        for (const t of Object.values(zone.tiers)) {
            allMobs = allMobs.concat(t.mobs || []);
        }
        return allMobs;
    }

    // Normal zone
    return zone.mobs || [];
}

/**
 * Get zones by tier
 * @param {number} tier - Tier number (1-3)
 * @returns {Object[]} Array of zones in that tier
 */
export function getZonesByTier(tier) {
    return Object.values(COMBAT_ZONES).filter(z => z.tier === tier);
}

/**
 * Get a mob by ID
 * @param {string} mobId - Mob ID
 * @returns {Object|null} Mob data
 */
export function getMob(mobId) {
    return Object.values(MOBS).find(m => m.id === mobId) || null;
}

/**
 * Get a random mob from a zone
 * @param {string} zoneId - Zone ID
 * @returns {Object|null} Random mob from zone
 */
export function getRandomMobForZone(zoneId, tier = null) {
    const mobs = getZoneMobs(zoneId, tier);
    if (mobs.length === 0) return null;

    const mobId = mobs[Math.floor(Math.random() * mobs.length)];
    return getMob(mobId);
}

/**
 * Get loot table for a zone
 * @param {string} zoneId - Zone ID
 * @returns {Object} Loot table with common and rare drops
 */
export function getZoneLoot(zoneId) {
    return ZONE_LOOT[zoneId] || { common: [], rare: [] };
}

/**
 * Get loot table for a specific mob (falls back to zone loot)
 * @param {string} mobId - Mob ID
 * @param {string} zoneId - Zone ID for fallback
 * @returns {Object} Loot table with common and rare drops
 */
export function getMobLoot(mobId, zoneId) {
    // Try mob-specific loot first
    if (MOB_LOOT[mobId]) {
        return MOB_LOOT[mobId];
    }
    // Fall back to zone loot for backwards compatibility
    return getZoneLoot(zoneId);
}

/**
 * Calculate mob damage (random between attack and attackMax)
 * @param {Object} mob - Mob object
 * @returns {number} Damage value
 */
export function calculateMobDamage(mob) {
    const min = mob.attack;
    const max = mob.attackMax || mob.attack;
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Get average XP per kill for a zone
 * @param {string} zoneId - Zone ID
 * @returns {number} Average XP
 */
export function getZoneAverageXP(zoneId, tier = null) {
    const mobs = getZoneMobs(zoneId, tier);
    if (mobs.length === 0) return 0;

    let totalXP = 0;
    for (const mobId of mobs) {
        const mob = getMob(mobId);
        if (mob) totalXP += mob.xp;
    }
    return Math.floor(totalXP / mobs.length);
}

// ==================== PYRE VEIL HELPER FUNCTIONS ====================

/**
 * Get mobs for a zone filtered by player's Pyre level
 * @param {string} zoneId - Zone ID
 * @param {number} pyreLevel - Player's Pyre level
 * @returns {string[]} Array of visible mob IDs
 */
export function getVisibleMobsForZone(zoneId, pyreLevel, tier = null) {
    const mobs = getZoneMobs(zoneId, tier);

    return mobs.filter(mobId => {
        const mob = getMob(mobId);
        if (!mob) return false;
        // Show if no pyreVeilLevel requirement OR player meets it
        return !mob.pyreVeilLevel || pyreLevel >= mob.pyreVeilLevel;
    });
}

/**
 * Get hidden mobs count for a zone (for UI indicator)
 * @param {string} zoneId - Zone ID
 * @param {number} pyreLevel - Player's Pyre level
 * @returns {number} Number of hidden mobs
 */
export function getHiddenMobCount(zoneId, pyreLevel, tier = null) {
    const mobs = getZoneMobs(zoneId, tier);

    return mobs.filter(mobId => {
        const mob = getMob(mobId);
        return mob?.pyreVeilLevel && pyreLevel < mob.pyreVeilLevel;
    }).length;
}

/**
 * Check if a zone is visible (not hidden by Pyre Veil)
 * @param {string} zoneId - Zone ID
 * @param {number} pyreLevel - Player's Pyre level
 * @returns {boolean}
 */
export function isZoneVisible(zoneId, pyreLevel) {
    const zone = getZone(zoneId);
    if (!zone) return false;
    if (!zone.pyreVeilHidden) return true;
    return pyreLevel >= zone.pyreLevel;
}

/**
 * Get a random visible mob from a zone (respects Pyre Veil)
 * @param {string} zoneId - Zone ID
 * @param {number} pyreLevel - Player's Pyre level
 * @returns {Object|null} Random visible mob
 */
export function getRandomVisibleMobForZone(zoneId, pyreLevel, tier = null) {
    const visibleMobs = getVisibleMobsForZone(zoneId, pyreLevel, tier);

    if (visibleMobs.length === 0) return null;

    const mobId = visibleMobs[Math.floor(Math.random() * visibleMobs.length)];
    return getMob(mobId);
}

/**
 * Get the next hidden mob that will be revealed for a zone
 * @param {string} zoneId - Zone ID
 * @param {number} pyreLevel - Player's current Pyre level
 * @returns {Object|null} { mob, pyreRequired } or null if none hidden
 */
export function getNextHiddenMob(zoneId, pyreLevel, tier = null) {
    const mobs = getZoneMobs(zoneId, tier);

    let nextMob = null;
    let lowestReq = Infinity;

    for (const mobId of mobs) {
        const mob = getMob(mobId);
        if (mob?.pyreVeilLevel && pyreLevel < mob.pyreVeilLevel) {
            if (mob.pyreVeilLevel < lowestReq) {
                lowestReq = mob.pyreVeilLevel;
                nextMob = mob;
            }
        }
    }

    return nextMob ? { mob: nextMob, pyreRequired: lowestReq } : null;
}

// ==================== BOSS MECHANICS ====================
// Data-driven boss mechanic definitions. Each boss key maps to an object
// describing phases, enrage timers, periodic effects, and damage floors.
// Logic is processed in combat.js processBossMechanics().

export const BOSS_MECHANICS = {
    // ===== Tier 1: Dungeon Bosses =====

    ashen_warden: {
        isBoss: true,
        name: 'Ashen Warden',
        damageFloor: 0,
        enrage: {
            timer: 90000,      // 90s until first enrage
            damageMult: 1.5,   // +50% damage
            speedMult: 0.75,   // +25% speed (lower interval = faster)
            stages: [
                { timer: 90000, damageMult: 1.5, speedMult: 0.75 },
                { timer: 120000, damageMult: 2.0, speedMult: 0.75 }  // +100% total at 120s
            ]
        },
        phases: null,
        periodicEffects: null
    },

    pyre_guardian: {
        isBoss: true,
        name: 'Pyre Guardian',
        damageFloor: 0,
        enrage: null,
        phases: [
            {
                id: 'normal',
                name: 'Normal',
                hpThreshold: 1.0,  // Active from 100% HP
                mobMods: {}
            },
            {
                id: 'defense_phase',
                name: 'Defense Phase',
                hpThreshold: 0.5,  // Active below 50% HP
                duration: 30000,   // Lasts 30s then reverts to normal mods
                mobMods: { defenseBonus: 15 }
            }
        ],
        periodicEffects: [
            {
                id: 'healing_pulse',
                name: 'Healing Pulse',
                type: 'heal_mob',
                interval: 20000,   // Every 20s
                amount: 60,        // Heal 60 HP
                activePhase: null   // Active in all phases
            }
        ]
    },

    flame_eternal: {
        isBoss: true,
        name: 'Flame Eternal',
        damageFloor: 15,
        enrage: null,
        phases: [
            {
                id: 'phase1',
                name: 'Phase 1',
                hpThreshold: 1.0,   // 100-66% HP
                mobMods: {}
            },
            {
                id: 'phase2',
                name: 'Scorching Aura',
                hpThreshold: 0.66,  // 66-33% HP
                mobMods: { speedMult: 0.8 },   // +20% speed (faster attacks)
                playerEffects: [
                    { type: 'dot', damage: 5, interval: 3000, name: 'Scorching Aura' }
                ]
            },
            {
                id: 'phase3',
                name: 'Ignition',
                hpThreshold: 0.33,  // 33-0% HP
                mobMods: { damageMult: 1.4, defenseBonus: -15 },  // +40% dmg, -15 def
                playerEffects: [
                    { type: 'dot', damage: 5, interval: 3000, name: 'Scorching Aura' }
                ]
            }
        ],
        periodicEffects: null
    },

    // ===== Tier 2: Set Bosses =====

    searing_matriarch: {
        isBoss: true,
        name: 'Searing Matriarch',
        damageFloor: 18,
        enrage: null,
        phases: [
            {
                id: 'normal',
                name: 'Normal',
                hpThreshold: 1.0,
                mobMods: {}
            },
            {
                id: 'arcane_disruption',
                name: 'Arcane Disruption',
                hpThreshold: 0.5,   // Below 50% HP
                mobMods: { speedMult: 0.8 },  // +20% attack speed
                playerEffects: [
                    { type: 'true_dot', damage: 8, interval: 5000, name: 'Arcane Disruption' }
                ]
            }
        ],
        periodicEffects: null
    },

    molten_colossus: {
        isBoss: true,
        name: 'Molten Colossus',
        damageFloor: 20,
        enrage: null,
        phases: [
            {
                id: 'normal',
                name: 'Normal',
                hpThreshold: 1.0,
                mobMods: {}
            },
            {
                id: 'fortify',
                name: 'Fortify',
                hpThreshold: 0.6,   // Below 60% HP
                mobMods: { defenseBonus: 10 }
            },
            {
                id: 'fortify_intense',
                name: 'Fortify (Intense)',
                hpThreshold: 0.3,   // Below 30% HP
                mobMods: { defenseBonus: 20 }
            }
        ],
        periodicEffects: [
            {
                id: 'fortify_heal',
                name: 'Regeneration',
                type: 'heal_mob',
                interval: 15000,    // Every 15s
                amount: 40,         // Heal 40 HP (phase 1)
                activePhase: 'fortify',  // Only while fortified
                intensify: {
                    phase: 'fortify_intense',
                    amount: 80      // Heal 80 HP when intensified
                }
            }
        ]
    },

    cindered_knight: {
        isBoss: true,
        name: 'Cindered Knight',
        damageFloor: 22,
        enrage: {
            timer: 75000,       // 75s until enrage
            damageMult: 1.4,    // +40% damage
            speedMult: 0.8,     // +20% speed
            stages: [
                { timer: 75000, damageMult: 1.4, speedMult: 0.8 }
            ]
        },
        phases: [
            {
                id: 'normal',
                name: 'Normal',
                hpThreshold: 1.0,
                mobMods: {}
            },
            {
                id: 'berserker',
                name: 'Berserker',
                hpThreshold: 0.4,   // Below 40% HP
                mobMods: {
                    damageMult: 1.6,        // +60% total damage
                    ignoreDefensePercent: 0.3  // Ignores 30% of player defense
                }
            }
        ],
        periodicEffects: null
    },

    // ===== Tier 3: Convergence =====

    convergence_herald: {
        isBoss: true,
        name: 'Convergence Herald',
        damageFloor: 25,
        enrage: {
            timer: 180000,      // 180s (3 min) until enrage
            damageMult: 1.0,    // No damage increase from enrage itself
            speedMult: 0.5,     // +100% speed (double speed)
            stages: [
                { timer: 180000, damageMult: 1.0, speedMult: 0.5 }
            ]
        },
        phases: [
            {
                id: 'phase1',
                name: 'Phase 1',
                hpThreshold: 1.0,   // 100-75% HP
                mobMods: {}
            },
            {
                id: 'void_barrier',
                name: 'Void Barrier',
                hpThreshold: 0.75,  // 75-50% HP
                mobMods: { speedMult: 0.85 },  // +15% attack speed
                playerEffects: [
                    { type: 'damage_reduction', value: 0.25, name: 'Void Barrier' }  // Player deals 25% less damage
                ]
            },
            {
                id: 'convergence_pulse',
                name: 'Convergence Pulse',
                hpThreshold: 0.5,   // 50-25% HP
                mobMods: { speedMult: 0.85 },
                playerEffects: []  // Void Barrier drops - player damage returns to normal
            },
            {
                id: 'final_stand',
                name: 'Final Stand',
                hpThreshold: 0.25,  // 25-0% HP
                mobMods: {
                    damageMult: 1.3,    // +30% all stats
                    defenseBonus: Math.floor(75 * 0.3),  // +30% of 75 base def = +22
                    speedMult: 0.77     // +30% speed
                },
                playerEffects: []  // Void Barrier drops
            }
        ],
        periodicEffects: [
            {
                id: 'convergence_pulse_damage',
                name: 'Convergence Pulse',
                type: 'true_damage_player',
                interval: 10000,    // Every 10s
                amount: 40,         // 40 true damage (ignores defense)
                activePhase: 'convergence_pulse'
            },
            {
                id: 'convergence_heal',
                name: 'Convergence Heal',
                type: 'heal_mob',
                interval: 10000,    // Every 10s (same timing as pulse)
                amount: 50,         // Heal 50 HP (reduced from 100)
                activePhase: 'convergence_pulse'
            }
        ]
    }
};

/**
 * Get boss mechanics data for a mob
 * @param {string} mobId - Mob ID
 * @returns {Object|null} Boss mechanics or null if not a boss
 */
export function getBossMechanics(mobId) {
    return BOSS_MECHANICS[mobId] || null;
}

/**
 * Check if a mob is a boss (has mechanics)
 * @param {string} mobId - Mob ID
 * @returns {boolean}
 */
export function isBoss(mobId) {
    return !!BOSS_MECHANICS[mobId];
}

// ==================== MOB MECHANICS ====================
// Data-driven mechanic definitions for non-boss mobs
// Processed in combat tick — all effects clear on mob death or zone change

export const MOB_MECHANICS = {
    // Player debuffs
    goblin_trapper: {
        type: 'snare',
        chance: 0.15,
        slowPercent: 0.20,
        duration: 5000,
        description: 'Slows your attack speed by 20% for 5s'
    },
    goblin_firestarter: {
        type: 'fireDot',
        damage: 2,
        interval: 3000,
        duration: 6000,
        description: 'Burns for 2 damage every 3s'
    },
    bandit_hexer: {
        type: 'curse',
        chance: 0.20,
        defenseReduction: 3,
        duration: 8000,
        description: 'Reduces your defense by 3 for 8s'
    },
    cave_spider: {
        type: 'poison',
        damage: 3,
        interval: 2000,
        duration: 8000,
        description: 'Poisons for 3 damage every 2s'
    },
    hellhound: {
        type: 'fireDot',
        damage: 4,
        interval: 2000,
        duration: 6000,
        description: 'Burns for 4 damage every 2s'
    },
    scaled_marauder: {
        type: 'armorShred',
        defensePerStack: 2,
        maxStacks: 3,
        duration: 8000,
        description: 'Shreds 2 defense per hit, max 3 stacks'
    },

    // Mob self-buffs
    cursed_knight: {
        type: 'selfHeal',
        chance: 0.20,
        healAmount: 15,
        description: '20% chance to heal 15 HP per attack'
    },
    pit_fiend: {
        type: 'enrage',
        hpThreshold: 0.30,
        speedBoost: 0.50,
        description: '+50% attack speed below 30% HP'
    },
    void_stalker: {
        type: 'phaseShift',
        chance: 0.20,
        description: '20% chance to dodge your attack'
    },
    entropy_maw: {
        type: 'absorption',
        healPercent: 0.25,
        healCap: 20,
        description: 'Heals 25% of damage dealt to you (max 20)'
    },
    temporal_shade: {
        type: 'timeWarp',
        minSpeed: 2000,
        maxSpeed: 4000,
        description: 'Randomizes attack speed each cycle'
    },
    sanctum_guardian: {
        type: 'damageReflection',
        reflectPercent: 0.10,
        reflectCap: 8,
        description: 'Reflects 10% of your damage back (max 8)'
    }
};

/**
 * Get mob mechanic data
 * @param {string} mobId - Mob ID
 * @returns {Object|null} Mechanic data or null
 */
export function getMobMechanic(mobId) {
    return MOB_MECHANICS[mobId] || null;
}

export default {
    COMBAT_ZONES,
    MOBS,
    ZONE_LOOT,
    MOB_LOOT,
    COMBAT_TIERS,
    BOSS_MECHANICS,
    getZone,
    getZoneMobs,
    getZonesByTier,
    getMob,
    getRandomMobForZone,
    getZoneLoot,
    getMobLoot,
    calculateMobDamage,
    getZoneAverageXP,
    getVisibleMobsForZone,
    getHiddenMobCount,
    isZoneVisible,
    getRandomVisibleMobForZone,
    getNextHiddenMob,
    getBossMechanics,
    isBoss,
    MOB_MECHANICS,
    getMobMechanic
};
